import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, BoxIcon } from 'lucide-react';
import { Badge } from './Badge';
export interface ServiceFeature {
  title: string;
  description: string;
  icon: BoxIcon;
}
export interface ServicePageProps {
  badgeText: string;
  badgeIcon: React.ReactNode;
  titleStart: string;
  titleAccent: string;
  titleAccentColor: 'blue' | 'purple' | 'green' | 'amber';
  subtitle: string;
  heroIcon: BoxIcon;
  heroIconColor: string;
  heroIconBg: string;
  checklist: string[];
  features: ServiceFeature[];
  processSteps: {
    number: string;
    title: string;
    description: string;
  }[];
  ctaTitle: string;
  ctaSubtitle: string;
}
const accentColorMap = {
  blue: 'text-brand-blue',
  purple: 'text-brand-purple',
  green: 'text-green-600',
  amber: 'text-amber-600'
};
export function ServicePage({
  badgeText,
  badgeIcon,
  titleStart,
  titleAccent,
  titleAccentColor,
  subtitle,
  heroIcon: HeroIcon,
  heroIconColor,
  heroIconBg,
  checklist,
  features,
  processSteps,
  ctaTitle,
  ctaSubtitle
}: ServicePageProps) {
  return <div className="w-full" data-id="element-199">
      {/* Hero */}
      <section className="relative pt-20 pb-24 overflow-hidden bg-gradient-to-b from-white via-slate-50 to-indigo-50/40" data-id="element-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-201">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex flex-col items-center" data-id="element-202">
            <Badge icon={badgeIcon} text={badgeText} data-id="element-203" />
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6 text-[#0B1020]" data-id="element-204">
              {titleStart}{' '}
              <span className={accentColorMap[titleAccentColor]} data-id="element-205">
                {titleAccent}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed max-w-2xl" data-id="element-206">
              {subtitle}
            </p>
            <div className="grid sm:grid-cols-2 gap-y-3 gap-x-8 mb-10 text-left" data-id="element-207">
              {checklist.map(item => <div key={item} className="flex items-center gap-2 text-slate-600 font-medium" data-id="element-208">
                  <CheckCircle2 className="w-5 h-5 text-brand-blue shrink-0" data-id="element-209" />
                  <span data-id="element-210">{item}</span>
                </div>)}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-211">
              <Link to="/contact" className="bg-brand-blue hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2 group" data-id="element-212">
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-213" />
              </Link>
              <Link to="/contact" className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center shadow-sm" data-id="element-214">
                Talk to an Expert
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white" data-id="element-215">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-216">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-217">
            <h2 className="text-3xl md:text-4xl font-black mb-4" data-id="element-218">
              What we deliver
            </h2>
            <p className="text-lg text-slate-600" data-id="element-219">
              Outcomes-focused capabilities, built end-to-end by our specialist
              team.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-220">
            {features.map((feature, i) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="p-6 rounded-2xl border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all bg-white" data-id="element-221">
                <div className={`w-12 h-12 rounded-xl ${heroIconBg} ${heroIconColor} flex items-center justify-center mb-5`} data-id="element-222">
                  <feature.icon className="w-6 h-6" data-id="element-223" />
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-224">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm" data-id="element-225">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-slate-50" data-id="element-226">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-228">
            <h2 className="text-3xl md:text-4xl font-black mb-4" data-id="element-229">
              How we work
            </h2>
            <p className="text-lg text-slate-600" data-id="element-230">
              A proven, transparent process from kickoff to launch.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-231">
            {processSteps.map((step, i) => <motion.div key={step.number} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm" data-id="element-232">
                <div className={`text-5xl font-black ${accentColorMap[titleAccentColor]} mb-4 opacity-90`} data-id="element-233">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold mb-2" data-id="element-234">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-235">
                  {step.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-236">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 to-brand-purple/20" data-id="element-237" />
        <div className="max-w-4xl mx-auto px-4 relative z-10 text-center" data-id="element-238">
          <h2 className="text-4xl md:text-5xl font-black mb-6 text-white" data-id="element-239">
            {ctaTitle}
          </h2>
          <p className="text-xl text-slate-300 mb-10" data-id="element-240">{ctaSubtitle}</p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-blue hover:bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-blue-600/20" data-id="element-241">
            Start a Conversation
            <ArrowRight className="w-5 h-5" data-id="element-242" />
          </Link>
        </div>
      </section>
    </div>;
}